import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { AxiosConnect } from "../services/axios.connect";
import { useAppContext } from "../context/app.context";
import { handleError } from "../utils/errorHandler";

const DeleteModal = ({ show, onCancel, onConfirm, productName }) => {
  const [step, setStep] = useState(1);

  const handleConfirm = async () => {
    if (step === 1) {
      setStep(2);
    } else {
      await onConfirm();
      setStep(1);
      onCancel();
    }
  };

  const handleCancel = () => {
    setStep(1);
    onCancel();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-96 shadow-lg">
        {step === 1 && (
          <>
            <h2 className="text-xl font-bold mb-4">Confirm Deletion</h2>
            <p className="mb-4">
              Are you sure you want to delete{" "}
              <span className="font-bold">{productName}</span>?
            </p>
            <div className="flex justify-end">
              <button
                onClick={handleCancel}
                className="mr-4 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded"
              >
                Yes, Delete
              </button>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <h2 className="text-xl font-bold mb-4">Final Confirmation</h2>
            <p className="mb-4">
              This action cannot be undone. Do you really want to permanently
              delete <span className="font-bold">{productName}</span>?
            </p>
            <div className="flex justify-end">
              <button
                onClick={handleCancel}
                className="mr-4 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="bg-red-700 hover:bg-red-800 text-white font-bold py-2 px-4 rounded"
              >
                Delete Permanently
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [modalProduct, setModalProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState("");
  const { showAlert } = useAppContext();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data } = await AxiosConnect.get("/v1/product/products/admin");
      setProducts(data?.data || []);
    } catch (error) {
      console.error("Error fetching products", error);
      showAlert("error", "Error", "Unable to fetch products");
    }
  };

  const disableProduct = async (id, value) => {
    try {
      await AxiosConnect.put(`/v1/product/edit/${id}`, { disabled: !value, twoFactorToken });
      showAlert("success", "Product disabled", "Product Disabled");
      fetchProducts();
    } catch (error) {
      const errorMessage = handleError(error);
      console.error("Error disable product", errorMessage);
      showAlert("warning", "Error", "Unable to disable product. " + errorMessage);
    }
  };


  const handleDelete = async (id) => {
    try {
      await AxiosConnect.delete(`/v1/product/dlt/${id}/${twoFactorToken}`);
      fetchProducts();
      setShowModal(false);
      setModalProduct(null);
      showAlert("success", "Product deleted", "Product deleted");
    } catch (error) {
      const errorMessage = handleError(error);
      console.error("Error deleting product", errorMessage);
      showAlert("error", "Error", "Unable to delete product. " + errorMessage);
    }
  };

  const openDeleteModal = (product) => {
    setModalProduct(product);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalProduct(null);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center mb-6">
        <h1 className="text-3xl font-bold">Axgura Product</h1>

        <div className="flex flex-col sm:flex-row justify-end items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="flex items-center space-x-2">
            <span>Auth Code:</span>
            <input
              value={twoFactorToken}
              onChange={(e) => setTwoFactorToken(e.target.value)}
              className="border border-blue-600 p-2 font-bold py-1 px-3 rounded"
            />
          </div>
          <a
            href="/product/new"
            className="bg-blue-600 text-white font-bold py-1 px-3 rounded"
          >
            Create product
          </a>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {products &&
              products.map((product) => (
                <tr key={product._id} className={product.disabled ? "opacity-60": ""}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {product.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {product.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <Link
                      to={`/product/${product.product_id}`}
                      className="mr-2 bg-blue-500 hover:bg-blue-600 text-white font-bold py-1 px-3 rounded"
                    >
                      Edit
                    </Link>

                    <button
                      onClick={() => disableProduct(product?.product_id, product.disabled)}
                      className="bg-gray-500 m-2 hover:bg-gray-600 text-white font-bold py-1 px-3 rounded"
                    >
                      { product.disabled ? 'Enable' : 'Disable' }
                    </button>
                    
                    <button
                      onClick={() => openDeleteModal(product)}
                      className="bg-red-500 hover:bg-red-600 text-white font-bold py-1 px-3 rounded"
                    >
                      Delete
                    </button>

                    
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {modalProduct && (
        <DeleteModal
          show={showModal}
          productName={modalProduct.name}
          onCancel={closeModal}
          onConfirm={() => handleDelete(modalProduct.product_id)}
        />
      )}
    </div>
  );
};

export default ProductList;
